import { RadioGroup as RadioPrimitive } from 'radix-ui'
import { useId } from 'react'
import { cn } from '@/utils/cn'

export interface RadioOption {
  value: string
  label: string
  description?: string
}

interface RadioGroupProps {
  value: string | undefined
  onValueChange: (value: string) => void
  options: RadioOption[]
  name?: string
  orientation?: 'horizontal' | 'vertical'
  /** "cards" renders each option as a selectable tile. */
  appearance?: 'default' | 'cards'
  className?: string
  'aria-label'?: string
  'aria-labelledby'?: string
}

export function RadioGroup({
  value,
  onValueChange,
  options,
  orientation = 'vertical',
  appearance = 'default',
  className,
  ...aria
}: RadioGroupProps) {
  const id = useId()
  return (
    <RadioPrimitive.Root
      value={value}
      onValueChange={onValueChange}
      orientation={orientation}
      className={cn(
        'flex gap-3',
        orientation === 'vertical' ? 'flex-col' : 'flex-wrap',
        appearance === 'cards' && 'grid gap-2 sm:grid-cols-2',
        className,
      )}
      {...aria}
    >
      {options.map((o) => {
        const itemId = `${id}-${o.value}`
        return (
          <label
            key={o.value}
            htmlFor={itemId}
            className={cn(
              'flex cursor-pointer items-start gap-2.5 text-sm text-ink-soft',
              appearance === 'cards' &&
                'rounded-control border border-line bg-surface p-3 transition-colors hover:border-brand-300 has-[[data-state=checked]]:border-brand-500 has-[[data-state=checked]]:bg-brand-50/60 has-[[data-state=checked]]:ring-1 has-[[data-state=checked]]:ring-brand-500',
            )}
          >
            <RadioPrimitive.Item
              id={itemId}
              value={o.value}
              className="mt-0.5 grid size-[1.125rem] shrink-0 place-items-center rounded-full border border-line-strong bg-surface shadow-xs transition-colors hover:border-brand-400 data-[state=checked]:border-brand-600"
            >
              <RadioPrimitive.Indicator className="size-2.5 rounded-full bg-brand-600" />
            </RadioPrimitive.Item>
            <span>
              <span className="font-medium text-ink">{o.label}</span>
              {o.description ? (
                <span className="mt-0.5 block text-xs text-muted">{o.description}</span>
              ) : null}
            </span>
          </label>
        )
      })}
    </RadioPrimitive.Root>
  )
}
